import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { BG_CARD, BORDER_LIGHT, TEXT_DARK, TEXT_MUTED, PRIMARY } from '../components/legacy-ball/table-management/designTokens';
import { CountdownTimer } from '../components/my-weekend/CountdownTimer';
import { TicketCard } from '../components/my-weekend/TicketCard';
import { SchedulePanel } from '../components/my-weekend/SchedulePanel';
import { MealStatusCard } from '../components/my-weekend/MealStatusCard';
import { SeatingCard } from '../components/my-weekend/SeatingCard';
import { AnnouncementsFeed } from '../components/my-weekend/AnnouncementsFeed';
import { RsvpCard, EventInfoData } from '../components/my-weekend/RsvpCard';
import { CheckInQRCard } from '../components/my-weekend/CheckInQRCard';
import { CheckInCelebration } from '../components/my-weekend/CheckInCelebration';
import { LegacyBallNextStep } from '../components/my-weekend/LegacyBallNextStep';
import { LanguageToggle } from '../components/LanguageToggle';
import { EmailUpgradeModal } from '../components/my-weekend/EmailUpgradeModal';

interface TicketInfo {
  registrationId: string;
  tierName: string;
  priceAtPurchase: number;
  status: 'Active' | 'Cancelled';
  referenceId: string;
  orderReferenceId?: string | null;
  checkedIn?: boolean;
}

interface EventInfo {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueName: string;
  venueAddress?: string;
  rsvpStatus?: string | null;
  mealInfo?: string | null;
  parkingInfo?: string | null;
  parkingImageUrl?: string | null;
  transportInfo?: string | null;
}

interface MealInfo {
  mealOptionName: string;
  mealOptionIcon: string | null;
  mealOptionColor: string | null;
}

interface SeatInfo {
  tableNumber: number;
  seatNumber: number;
  tableName?: string;
  zoneName?: string;
  gridAddress?: string;
  suggestedEntrance?: string;
  tableGuests?: Array<{ fullName: string; abbreviatedName: string; seatNumber: number; isCurrentUser: boolean; mealSelection?: string | null; orderReferenceId?: string | null; tableRequestGroupId?: string | null }>;
}

interface AnnouncementInfo {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

interface MyWeekendResponse {
  ticket: TicketInfo | null;
  isCheckedIn?: boolean;
  events: EventInfo[];
  meal: MealInfo | null;
  seat: SeatInfo | null;
  announcements: AnnouncementInfo[];
  purchases: Array<{ id: string; title: string; category: string; amount: number | null }>;
  currentEvent: NowEventInfo | null;
  nextEvent: NowEventInfo | null;
  chapterAffiliation?: string | null;
  isChapterMember?: boolean;
  isSynthetic?: boolean;
  isVendor?: boolean;
  vendorBusinessName?: string | null;
  showTableGuests?: boolean;
}

interface NowEventInfo {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  venueName: string;
}

/**
 * Extract HH:MM from stored value (stored as Eastern, no conversion needed).
 */
function formatEventTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number;
  let minutes: number;

  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function getEarliestUpcomingEventStartTime(events: EventInfo[]): string | null {
  const now = new Date();
  const upcoming = events
    .filter((e) => new Date(e.startTime) > now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  return upcoming.length > 0 ? upcoming[0].startTime : null;
}

const KRIO_GREETINGS = {
  morning: ['Ow de bodi! Gud mornin', 'Kusheh! Mornin tayim', 'Eh ya! Gud mornin'],
  afternoon: ['Ow de bodi! Gud aftanun', 'Kusheh! Aftanun tayim', 'Na so! Gud aftanun'],
  evening: ['Ow de bodi! Gud ivnin', 'Kusheh! Ivnin tayim', 'Eh ya! Gud ivnin'],
};
const KRIO_SUBTITLES = [
  'Dis na yu weekend plan. Enitin yu nid de ya.',
  'Yu weekend de ya. Mek wi enjoy am!',
  'Welkam! Na ya ɔl yu weekend infɔmeshɔn de.',
];

function getKrioGreeting(firstName: string): string {
  const hour = new Date().getHours();
  const period = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const greetings = KRIO_GREETINGS[period];
  const greeting = greetings[Math.floor(Math.random() * greetings.length)];
  return `${greeting}, ${firstName}!`;
}

function getKrioSubtitle(): string {
  return KRIO_SUBTITLES[Math.floor(Math.random() * KRIO_SUBTITLES.length)];
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1rem', maxWidth: '1000px', margin: '0 auto', overflowX: 'hidden' as const, boxSizing: 'border-box' as const, width: '100%' },
  title: { color: '#1a6b3c', fontSize: '1.4rem', margin: '0 0 0.75rem 0' },
  loading: { textAlign: 'center', padding: '2rem', color: '#666', fontSize: '1rem' },
  error: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    color: '#d32f2f',
  },
  errorMessage: { fontSize: '1rem', marginBottom: '1rem' },
  retryBtn: {
    padding: '0.6rem 1.2rem',
    minHeight: '44px',
    minWidth: '44px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
};

export function MyWeekendPage() {
  const { accessToken, user } = useAuth();
  const [data, setData] = useState<MyWeekendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasPresenterAccess, setHasPresenterAccess] = useState(false);
  const [openQuestionCount, setOpenQuestionCount] = useState(0);
  const [areaExpanded, setAreaExpanded] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if user has linked presenter portal access
  useEffect(() => {
    if (!accessToken) return;
    apiClient<{ token: string; openQuestionCount?: number }>('/presenter-portal/my-access')
      .then((data) => { setHasPresenterAccess(true); setOpenQuestionCount(data.openQuestionCount || 0); })
      .catch(() => {});
  }, [accessToken]);

  const fetchData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<MyWeekendResponse>('/my-weekend');
      setData(response);
    } catch {
      setError('Unable to load your weekend data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="attendee-page" style={styles.page}>
        <div style={styles.loading}>Loading your weekend...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="attendee-page" style={styles.page}>
        <div style={styles.error}>
          <p style={styles.errorMessage}>{error}</p>
          <button style={styles.retryBtn} onClick={fetchData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const targetDate = data ? getEarliestUpcomingEventStartTime(data.events) : null;

  const isKrio = user?.preferredLanguage === 'krio';
  const firstName = user?.fullName?.split(' ')[0] || '';

  return (
    <div className="attendee-page" style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          {isKrio ? (
            <>
              <h1 style={styles.title}>{getKrioGreeting(firstName)}</h1>
              <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#555', fontStyle: 'italic' }}>{getKrioSubtitle()}</p>
            </>
          ) : (
            <h1 style={styles.title}>My Weekend {user?.fullName ? `(${user.fullName})` : ''}{data?.isChapterMember && <img src="/images/KDUGaLogo.png" alt="KDU Georgia" style={{ width: '24px', height: '24px', borderRadius: '4px', marginLeft: '8px', verticalAlign: 'middle' }} />}</h1>
          )}
        </div>
        <LanguageToggle />
      </div>

      {/* Notification Summary */}
      <NotificationSummaryCard />

      {/* Check-in celebration animation */}
      <CheckInCelebration isCheckedIn={data?.isCheckedIn || data?.ticket?.checkedIn || false} />

      <CountdownTimer targetDate={targetDate} />

      {/* What's Happening Now Widget */}
      {data && (data.currentEvent || data.nextEvent) && (
        <div style={{
          marginBottom: '1.25rem',
          padding: '1.25rem 1.5rem',
          background: data.currentEvent
            ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 50%, #86efac 100%)'
            : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 50%, #a7f3d0 100%)',
          border: '2px solid #16a34a',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(22, 163, 106, 0.15)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.35rem' }}>{data.currentEvent ? '🔴' : '📅'}</span>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#15803d', letterSpacing: '0.02em' }}>
              {data.currentEvent ? 'Happening Now' : 'Coming Up'}
            </span>
          </div>
          {data.currentEvent && (
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f2d1f' }}>{data.currentEvent.name}</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', marginTop: '0.3rem' }}>
                {formatEventTime(data.currentEvent.startTime)} – {formatEventTime(data.currentEvent.endTime)} · {data.currentEvent.venueName}
              </div>
              {(() => {
                const matchingEvent = data.events.find(e => e.id === data.currentEvent!.id);
                const venueAddress = matchingEvent?.venueAddress;
                if (venueAddress) {
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueAddress)}`;
                  return (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', marginTop: '0.4rem', fontSize: '0.8rem', color: '#1a73e8', textDecoration: 'none', fontWeight: 500 }}
                    >
                      📍 Get Directions
                    </a>
                  );
                }
                return null;
              })()}
            </div>
          )}
          {!data.currentEvent && data.nextEvent && (
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f2d1f' }}>{data.nextEvent.name}</div>
              <div style={{ fontSize: '0.85rem', color: '#374151', marginTop: '0.3rem' }}>
                Starts at {formatEventTime(data.nextEvent.startTime)} · {data.nextEvent.venueName}
              </div>
              {/* Countdown */}
              {(() => {
                const now = new Date();
                const start = new Date(data.nextEvent!.startTime);
                const diffMs = start.getTime() - now.getTime();
                if (diffMs > 0) {
                  const totalMinutes = Math.floor(diffMs / 60000);
                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;
                  const countdownText = hours > 0
                    ? `Starts in ${hours}h ${minutes}m`
                    : `Starts in ${minutes}m`;
                  return (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.9rem', fontWeight: 700, color: '#b45309', backgroundColor: 'rgba(251, 191, 36, 0.2)', display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>
                      ⏱ {countdownText}
                    </div>
                  );
                }
                return null;
              })()}
              {/* Directions link */}
              {(() => {
                const matchingEvent = data.events.find(e => e.id === data.nextEvent!.id);
                const venueAddress = matchingEvent?.venueAddress;
                if (venueAddress) {
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueAddress)}`;
                  return (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-block', marginTop: '0.4rem', marginLeft: '0.5rem', fontSize: '0.8rem', color: '#1a73e8', textDecoration: 'none', fontWeight: 500 }}
                    >
                      📍 Get Directions
                    </a>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>
      )}
      {data && !data.currentEvent && !data.nextEvent && data.events.length > 0 && (
        <div style={{
          marginBottom: '1.25rem',
          padding: '1rem 1.25rem',
          backgroundColor: '#f9fafb',
          border: '2px solid #d1d5db',
          borderRadius: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.25rem' }}>📅</span>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>No more events today</span>
          </div>
        </div>
      )}

      {/* Legacy Ball Next Step Indicator */}
      {data && (
        <LegacyBallNextStep
          hasRegistration={data.events.some(e => e.name.toLowerCase().includes('legacy ball'))}
          hasTicket={!!data.ticket}
          ticketStatus={data.ticket?.status || null}
          isCheckedIn={data.isCheckedIn || data.ticket?.checkedIn || false}
        />
      )}

      {/* Check-In QR Code — shown when user has an active ticket */}
      {data?.ticket && data.ticket.status === 'Active' && (
        <div style={{ marginBottom: '0.75rem' }}>
          <CheckInQRCard
            registrationId={data.ticket.registrationId}
            guestName={user?.fullName || 'Guest'}
            tierName={data.ticket.tierName}
            referenceId={data.ticket.referenceId}
            orderReferenceId={data.ticket.orderReferenceId}
            zoneName={data.seat?.zoneName}
            tableName={data.seat?.tableName}
            tableNumber={data.seat?.tableNumber}
            seatNumber={data.seat?.seatNumber}
            isGaChapter={!!(data.isChapterMember)}
          />
        </div>
      )}

      {data && (
        <div className="my-weekend-grid">
          <div style={styles.leftColumn}>
            <TicketCard ticket={data.ticket} isCheckedIn={data.isCheckedIn || data.ticket?.checkedIn || false} />
            <SchedulePanel events={data.events} />
          </div>
          <div style={styles.rightColumn}>
            <MealStatusCard meal={data.meal} />
            <SeatingCard seat={data.seat} userRole={user?.role} userOrderRef={data.ticket?.orderReferenceId} showTableGuestNames={data.showTableGuests !== false} />
            <RsvpCard
              userRsvps={data.events.map(e => ({ id: e.id, rsvpStatus: e.rsvpStatus }))}
              eventInfoMap={data.events.reduce<Record<string, EventInfoData>>((acc, e) => {
                acc[e.id] = {
                  id: e.id,
                  name: e.name,
                  mealInfo: e.mealInfo ?? null,
                  parkingInfo: e.parkingInfo ?? null,
                  parkingImageUrl: e.parkingImageUrl ?? null,
                  transportInfo: e.transportInfo ?? null,
                };
                return acc;
              }, {})}
            />
            <AnnouncementsFeed announcements={data.announcements} />
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/nearby" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', textDecoration: 'none', color: '#1a6b3c', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          📍 Nearby Places
        </Link>
        <Link to={data?.ticket?.referenceId ? `/guest-card/${data.ticket.referenceId}` : '/my-info'} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', textDecoration: 'none', color: '#1a6b3c', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          🪪 My Guest Card
        </Link>
        {hasPresenterAccess && (
          <Link to="/presenter-portal" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px', textDecoration: 'none', color: '#92400e', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', position: 'relative' }}>
            🎤 Presenter Portal
            {openQuestionCount > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', borderRadius: '9px', backgroundColor: '#dc2626', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '0 4px' }}>{openQuestionCount}</span>
            )}
          </Link>
        )}
        {data?.isVendor && (
          <Link to="/my-vendor-space" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', textDecoration: 'none', color: '#166534', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            🏪 My Vendor Space
            {data.vendorBusinessName && (
              <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#4b5563' }}>({data.vendorBusinessName})</span>
            )}
          </Link>
        )}
        {(user?.role === 'Organizer' || user?.role === 'Admin') && (
          <Link to="/vendors" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '10px', textDecoration: 'none', color: '#5b21b6', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            🏪 Vendor Management
          </Link>
        )}
      </div>

      {/* Email Login Upgrade — only shown for synthetic (ticket-based) GA chapter members */}
      {data?.isChapterMember && data?.isSynthetic && (
        <div style={{ marginTop: '1rem', padding: '1rem 1.25rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e40af' }}>✉️ Upgrade to Email Login</div>
            <div style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '2px' }}>As a GA chapter member, you can switch from ticket-number login to your email.</div>
          </div>
          <button
            onClick={() => setShowUpgradeModal(true)}
            style={{ padding: '0.45rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
            aria-label="Upgrade to email login"
          >
            Upgrade
          </button>
        </div>
      )}

      <EmailUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={() => fetchData()}
      />

      {/* Before You Travel — health & packing reminder (shown to everyone) */}
      <div style={{ marginTop: '1rem', padding: '1.25rem', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '1.35rem' }}>🚨</span>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#92400e', margin: 0 }}>Before You Travel</h3>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: 1.5, margin: '0 0 0.6rem' }}>
          Many of us are traveling into Georgia from out of state. As you pack, please remember to bring
          all of your <strong>essential medications</strong> — such as blood pressure medication and insulin —
          with enough supply for your <strong>entire stay</strong>. 💊
        </p>
        <p style={{ fontSize: '0.82rem', color: '#78350f', lineHeight: 1.5, margin: 0 }}>
          Not traveling yourself? Please share this reminder with friends and family who are. Your health and
          well-being matter to us. ❤️
        </p>
        <div style={{ fontSize: '0.72rem', color: '#a16207', marginTop: '0.6rem', fontWeight: 600 }}>— KDU GA</div>
      </div>

      {/* Area Info — Attractions & Fun Facts (collapsible) */}
      <div style={{ marginTop: '1rem', padding: '1.25rem', backgroundColor: '#f0f7f2', border: '1px solid #d1fae5', borderRadius: '10px' }}>
        <button
          onClick={() => setAreaExpanded(!areaExpanded)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          aria-expanded={areaExpanded}
          aria-label="Toggle explore the area"
        >
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#166534', margin: 0 }}>🗺️ Explore the Area</h3>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>{areaExpanded ? '▲' : '▼'}</span>
        </button>
        <p style={{ fontSize: '0.78rem', color: '#555', margin: '0.25rem 0 0', lineHeight: 1.4 }}>
          {areaExpanded ? 'Drive times from the reunion hotel (Hilton Atlanta NE, Peachtree Corners)' : '25+ attractions · History · Universities · Arts · Sports · BeltLine — tap to explore'}
        </p>

        {areaExpanded && (
          <div style={{ marginTop: '0.75rem' }}>
            {/* History & Civil Rights */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>History & Civil Rights</div>
              {[
                { name: 'Martin Luther King Jr. National Historical Park', distance: '30 min', note: 'Birthplace, church, and memorial — civil rights landmark' },
                { name: 'National Center for Civil and Human Rights', distance: '30 min', note: 'Interactive civil rights and human rights museum' },
                { name: 'The Carter Center', distance: '25 min', note: 'Jimmy Carter Presidential Library and Museum' },
                { name: 'Atlanta History Center', distance: '30 min', note: 'Southern history, gardens, and historic houses' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>📌</span>
                  <span><strong>{a.name}</strong> ({a.distance}) — {a.note}</span>
                </div>
              ))}
            </div>

            {/* Universities */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Universities</div>
              {[
                { name: 'Emory University', distance: '25 min', note: 'Beautiful campus, Michael C. Carlos Museum (art & archaeology)' },
                { name: 'Georgia Institute of Technology', distance: '30 min', note: 'Top engineering school, campus tours available' },
                { name: 'Spelman College', distance: '30 min', note: 'Historic HBCU, renowned liberal arts college for women' },
                { name: 'Morehouse College', distance: '30 min', note: 'Historic HBCU, alma mater of Dr. Martin Luther King Jr.' },
                { name: 'Georgia State University', distance: '30 min', note: 'Downtown Atlanta campus, one of the most diverse in the US' },
                { name: 'SCAD Atlanta', distance: '30 min', note: 'Savannah College of Art and Design — galleries and exhibitions' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>🎓</span>
                  <span><strong>{a.name}</strong> ({a.distance}) — {a.note}</span>
                </div>
              ))}
            </div>

            {/* Arts & Culture */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Arts & Culture</div>
              {[
                { name: 'Georgia Aquarium', distance: '30 min', note: 'Largest aquarium in the Western Hemisphere' },
                { name: 'World of Coca-Cola', distance: '30 min', note: 'Interactive museum and tasting experience' },
                { name: 'High Museum of Art', distance: '30 min', note: 'Premier art museum in the Southeast' },
                { name: 'Fernbank Museum of Natural History', distance: '20 min', note: 'Dinosaurs, nature galleries, and IMAX theater' },
                { name: 'Atlanta Botanical Garden', distance: '30 min', note: 'Stunning gardens, canopy walk, and seasonal exhibits' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>🎨</span>
                  <span><strong>{a.name}</strong> ({a.distance}) — {a.note}</span>
                </div>
              ))}
            </div>

            {/* Sports & Entertainment */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Sports & Entertainment</div>
              {[
                { name: 'State Farm Arena', distance: '30 min', note: 'Home of the Atlanta Hawks (NBA), concerts and events' },
                { name: 'Truist Park', distance: '35 min', note: 'Home of the Atlanta Braves (MLB)' },
                { name: 'Mercedes-Benz Stadium', distance: '35 min', note: 'Atlanta Falcons (NFL) & Atlanta United (MLS)' },
                { name: 'Centennial Olympic Park', distance: '30 min', note: 'Downtown Atlanta gathering space' },
                { name: 'Centennial Yards', distance: '30 min', note: 'New mixed-use entertainment district in downtown Atlanta' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>🏟️</span>
                  <span><strong>{a.name}</strong> ({a.distance}) — {a.note}</span>
                </div>
              ))}
            </div>

            {/* Nature & Shopping */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Nature & Shopping</div>
              {[
                { name: 'Sugarloaf Mills Mall', distance: '5 min', note: 'Shopping, dining, and entertainment' },
                { name: 'Stone Mountain Park', distance: '15 min', note: 'Hiking, laser shows, and nature trails' },
                { name: 'Ponce City Market', distance: '25 min', note: 'Food hall, shops, rooftop amusements' },
                { name: 'Atlantic Station', distance: '30 min', note: 'Open-air shopping, dining, movies, and IKEA' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>🌿</span>
                  <span><strong>{a.name}</strong> ({a.distance}) — {a.note}</span>
                </div>
              ))}
            </div>

            {/* Atlanta BeltLine */}
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>Atlanta BeltLine (25–30 min from hotel)</div>
              <p style={{ fontSize: '0.78rem', color: '#555', margin: '0 0 0.4rem', lineHeight: 1.4 }}>
                A 22-mile multi-use trail connecting 45 neighborhoods — walk, bike, eat, and explore street art.
              </p>
              {[
                { name: 'Eastside Trail (Ponce City Market → Krog Street)', note: 'Most popular stretch — murals, restaurants, and the Krog Street Market food hall' },
                { name: 'Westside Trail (West End → Adair Park)', note: 'Lee + White food hall, Monday Night Brewing, public art installations' },
                { name: 'Krog Street Market', note: 'Indoor food hall and local shops at the BeltLine\'s Eastside midpoint' },
                { name: 'Paris on Ponce / Old Fourth Ward', note: 'Historic Fourth Ward Park, splash pad, skatepark, and BeltLine art' },
                { name: 'Piedmont Park (via BeltLine connector)', note: 'Atlanta\'s central park — trails, lake, skyline views' },
              ].map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', fontSize: '0.8rem', color: '#333', marginBottom: '3px' }}>
                  <span>🚶</span>
                  <span><strong>{a.name}</strong>{a.note ? ` — ${a.note}` : ''}</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: '0.78rem', color: '#555', borderTop: '1px solid #d1fae5', paddingTop: '0.5rem' }}>
              <strong>Did you know?</strong> Georgia is the "Peach State" · Atlanta has the world's busiest airport · Gwinnett County is one of the most diverse in the US
            </div>
          </div>
        )}
      </div>

      {/* Linked Purchases Section */}
      {data && data.purchases && data.purchases.length > 0 && (
        <div style={{ marginTop: '1.5rem', padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a6b3c', margin: '0 0 0.75rem 0' }}>🛒 My Purchases</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {data.purchases.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{p.title}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{p.category}</div>
                </div>
                {p.amount && (
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#16a34a' }}>${Number(p.amount).toFixed(2)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Tickets Section */}
      <MyTicketsSection />
    </div>
  );
}

interface MyTicketRow {
  id: string;
  ticketNumber: number;
  tierName: string;
  priceAtPurchase: number;
  status: string;
  guestName: string | null;
  meal: string | null;
  orderId: string | null;
  orderReferenceId: string | null;
  orderStatus: string | null;
  purchaserName: string | null;
  orderTicketCount: number;
}

interface OrderSiblingTicket {
  id: string;
  ticketNumber: number;
  tierName: string;
  priceAtPurchase: number;
  status: string;
  guestName: string | null;
  meal: string | null;
}

/* ─── Notification Summary Card ─── */

interface NotificationAnnouncement {
  id: string;
  title: string;
  createdAt: string;
  status: string;
}

function NotificationSummaryCard() {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [recent, setRecent] = useState<NotificationAnnouncement[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    async function fetchNotifications() {
      try {
        const [countData, announcements] = await Promise.all([
          apiClient<{ unreadCount: number }>('/announcements/unread-count'),
          apiClient<NotificationAnnouncement[]>('/announcements?limit=3'),
        ]);
        setUnreadCount(countData.unreadCount);
        setRecent(Array.isArray(announcements) ? announcements.slice(0, 3) : []);
      } catch {
        // Non-critical — silently degrade
      } finally {
        setLoaded(true);
      }
    }
    fetchNotifications();
  }, []);

  if (!loaded) return null;

  const cardStyle: React.CSSProperties = {
    marginBottom: '1rem',
    padding: '1rem 1.25rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
  };

  if (unreadCount === 0 && recent.length === 0) {
    return (
      <div style={cardStyle}>
        <span style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>🔔 No new notifications</span>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
        🔔 Notifications{unreadCount > 0 ? ` (${unreadCount} unread)` : ''}
      </div>
      {recent.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: '1.25rem', listStyle: 'disc' }}>
          {recent.map((a) => (
            <li key={a.id} style={{ fontSize: '0.8rem', color: TEXT_DARK, marginBottom: '0.25rem' }}>
              {a.title}
            </li>
          ))}
        </ul>
      )}
      <button
        onClick={() => {
          // On mobile, navigate to Announcements panel (index 4)
          window.dispatchEvent(new CustomEvent('navigate-to-panel', { detail: { index: 4 } }));
          // Fallback for desktop
          navigate('/announcements');
        }}
        style={{
          marginTop: '0.5rem',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: PRIMARY,
          padding: 0,
        }}
      >
        View All →
      </button>
    </div>
  );
}

/* ─── My Tickets Section ─── */

function MyTicketsSection() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<MyTicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [orderTickets, setOrderTickets] = useState<OrderSiblingTicket[]>([]);
  const [expandLoading, setExpandLoading] = useState(false);

  useEffect(() => {
    async function fetchTickets() {
      try {
        const result = await apiClient<MyTicketRow[]>('/legacy-ball/my-tickets');
        setTickets(result);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    }
    fetchTickets();
  }, []);

  if (loading || tickets.length === 0) return null;

  // Always show only the FIRST ticket (user's own) — regardless of how many the API returns
  const t = tickets[0];

  const canExpand = !!(
    t.orderId &&
    t.orderTicketCount > 1 &&
    t.purchaserName &&
    user?.fullName &&
    t.purchaserName.toLowerCase() === user.fullName.toLowerCase()
  );

  const handleExpandToggle = async () => {
    if (expanded) {
      setExpanded(false);
      setOrderTickets([]);
      return;
    }
    setExpanded(true);
    setExpandLoading(true);
    try {
      const result = await apiClient<OrderSiblingTicket[]>(`/legacy-ball/orders/${t.orderId}/tickets`);
      setOrderTickets(result);
    } catch {
      setOrderTickets([]);
    } finally {
      setExpandLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '1.5rem', padding: '1.25rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.75rem 0' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a6b3c', margin: 0 }}>🎟️ My Ticket</h3>
        {canExpand && (
          <button
            onClick={handleExpandToggle}
            style={{
              background: expanded ? '#e0f2fe' : '#f0f9ff',
              border: '1px solid #bfdbfe',
              cursor: 'pointer',
              padding: '3px 8px',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#1e40af',
              borderRadius: '12px',
              lineHeight: 1.2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
            aria-label={expanded ? 'Hide other tickets in order' : `View all ${t.orderTicketCount} tickets in this order`}
            title={expanded ? 'Hide order tickets' : `View all ${t.orderTicketCount} tickets in this order`}
          >
            👥 {expanded ? 'Hide' : `${t.orderTicketCount} in order`}
          </button>
        )}
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed' as const }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Order</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Ticket #</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Tier</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Guest</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Meal</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {t.orderReferenceId || '—'}
                  {canExpand && (
                    <button
                      onClick={handleExpandToggle}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px 4px',
                        fontSize: '0.7rem',
                        color: '#1e40af',
                        borderRadius: '4px',
                        lineHeight: 1,
                      }}
                      aria-label={expanded ? `Collapse order ${t.orderReferenceId}` : `View all ${t.orderTicketCount} tickets in order ${t.orderReferenceId}`}
                      title={expanded ? 'Hide order tickets' : `View all ${t.orderTicketCount} tickets in this order`}
                    >
                      {expanded ? '🔽' : '👥'}
                    </button>
                  )}
                </span>
              </td>
              <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>TKT-{String(t.ticketNumber).padStart(3, '0')}</td>
              <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>{t.tierName}</td>
              <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>{t.guestName || '—'}</td>
              <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>{t.meal || '—'}</td>
              <td style={{ padding: '0.5rem 0.75rem' }}>
                <span style={{
                  display: 'inline-block',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0.15rem 0.5rem',
                  borderRadius: '10px',
                  backgroundColor: t.orderStatus === 'Confirmed' ? '#dcfce7' : t.orderStatus === 'Pending' ? '#fef3c7' : '#f3f4f6',
                  color: t.orderStatus === 'Confirmed' ? '#166534' : t.orderStatus === 'Pending' ? '#92400e' : '#6b7280',
                }}>
                  {t.orderStatus || t.status}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Expanded Order Tickets — lazy-loaded cards */}
      {expanded && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e40af', marginBottom: '0.5rem' }}>
            👥 All Tickets in Order ({expandLoading ? '...' : `${orderTickets.length}`})
          </div>
          {expandLoading ? (
            <div style={{ fontSize: '0.75rem', color: '#6b7280', padding: '0.5rem 0' }}>Loading...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {orderTickets.map((st) => (
                <div
                  key={st.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0f2d1f' }}>
                      {st.guestName || 'Unassigned'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                      TKT-{String(st.ticketNumber).padStart(3, '0')} · {st.tierName}
                      {st.meal && ` · ${st.meal}`}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    padding: '0.15rem 0.5rem',
                    borderRadius: '10px',
                    backgroundColor: st.status === 'Active' ? '#dcfce7' : '#f3f4f6',
                    color: st.status === 'Active' ? '#166534' : '#6b7280',
                  }}>
                    {st.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


